import Button from "@mui/material/Button"
import { motion, useAnimation } from "framer-motion"
import React, { useState, useRef } from "react"

import ShineBorder from "./shine-border"

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

interface CloneAgentButtonProps {
  onClick: () => void
}

const CloneAgentButton: React.FC<CloneAgentButtonProps> = ({ onClick }) => {
  const [hovered, setHovered] = useState(false)
  const isHoveredRef = useRef(false)

  const leftPieControls = useAnimation()
  const armControls = useAnimation()
  const rightPieControls = useAnimation()

  const runAnimation = async () => {
    // Reset initial state.
    leftPieControls.set({ opacity: 1, rotate: 0 })
    armControls.set({ opacity: 0, scaleX: 0 })
    rightPieControls.set({
      scale: 0,
      x: 0,
      rotate: 0,
      background: "rgba(255,255,255,0.1)",
      boxShadow: "none",
    })

    // Left Pie: spin continuously.
    leftPieControls.start({
      rotate: 360,
      transition: { duration: 6, ease: "linear", repeat: Infinity },
    })

    // 200ms: Reveal the green arm (still zero length).
    await delay(200)
    if (!isHoveredRef.current) return
    await armControls.start({ opacity: 1, scaleX: 0, transition: { duration: 0.1 } })

    // 300ms: Extend the arm to full length.
    await delay(100)
    if (!isHoveredRef.current) return
    await armControls.start({ scaleX: 1, transition: { duration: 0.5, ease: "linear" } })

    // 350ms: Scale in the right pie.
    await delay(50)
    if (!isHoveredRef.current) return
    await rightPieControls.start({ scale: 1, transition: { duration: 0.5, ease: "easeOut" } })

    // 200ms later: Animate the right pie from its right-side position to the center.
    await delay(200)
    if (!isHoveredRef.current) return
    // Calculated x offset: current center is at ~109px, desired center is ~62.5px, so move left by ~46.5px.
    await rightPieControls.start({
      background: "conic-gradient(green 0% 50%, yellow 50% 70%, red 70% 100%)",
      transition: { duration: 1, ease: "easeOut" },
    })

    // Optionally, after a short delay, fade out the arm and left pie.
    await delay(300)
    if (!isHoveredRef.current) return
    await rightPieControls.start({
      boxShadow: "0 0 15px 5px rgba(0,255,0,0.8)",
      transition: { duration: 1, ease: "easeOut" },
    })
    armControls.start({ opacity: 0, transition: { duration: 1, ease: "easeInOut" } })

    leftPieControls.start({ opacity: 0, transition: { duration: 1, ease: "easeInOut" } })

    await delay(300)
    await rightPieControls.start({
      x: -46.5,
      transition: { duration: 1, ease: "easeOut" },
    })
    await rightPieControls.start({
      rotate: 360,
      transition: { duration: 6, ease: "linear", repeat: Infinity },
    })
  }

  const resetAnimation = () => {
    leftPieControls.stop()
    armControls.stop()
    rightPieControls.stop()
    leftPieControls.set({ opacity: 0, rotate: 0 })
    armControls.set({ opacity: 0, scaleX: 0 })
    rightPieControls.set({
      scale: 0,
      x: 0,
      rotate: 0,
      background: "rgba(255,255,255,0.1)",
      boxShadow: "none",
    })
  }

  const handleMouseEnter = () => {
    setHovered(true)
    isHoveredRef.current = true
    runAnimation()
  }

  const handleMouseLeave = () => {
    setHovered(false)
    isHoveredRef.current = false
    resetAnimation()
  }

  return (
    <ShineBorder
      color="var(--mui-palette-accent-main)"
      bgColor="var(--mui-palette-background-paper)"
    >
      <Button
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          position: "relative",
          overflow: "hidden",
          width: "125px",
          height: "40px",
          padding: 0,
        }}
      >
        {/* Show text only when not hovering */}
        {!hovered && (
          <span style={{ position: "relative", zIndex: 10, fontSize: "0.8rem" }}>Clone Agent</span>
        )}

        {/* Animation Overlay */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "125px",
            height: "40px",
            pointerEvents: "none",
          }}
        >
          {/* Left Pie Chart */}
          <motion.div
            animate={leftPieControls}
            initial={{ opacity: 0, rotate: 0 }}
            style={{
              position: "absolute",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              background: "conic-gradient(green 0% 50%, yellow 50% 70%, red 70% 100%)",
              left: "8px",
              top: "calc(50% - 8px)",
              transform: "translateY(-50%)",
              zIndex: 2,
            }}
          />

          {/* Green Arm */}
          <motion.div
            animate={armControls}
            initial={{ opacity: 0, scaleX: 0 }}
            style={{
              position: "absolute",
              height: "2px",
              width: "83px", // From left pie right edge (8+16=24px) to right pie left edge (125-8-16=101px)
              background: "rgba(0,255,0,1)",
              boxShadow:
                "0 0 10px rgba(0,255,0,1), 0 0 20px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.4)",
              top: "50%",
              left: "24px",
              transform: "translateY(-50%)",
              transformOrigin: "left center",
              zIndex: 1,
            }}
          />

          {/* Right Pie Chart */}
          <motion.div
            animate={rightPieControls}
            initial={{
              scale: 0,
              x: 0,
              rotate: 0,
              background: "rgba(255,255,255,0.1)",
              boxShadow: "none",
            }}
            style={{
              position: "absolute",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              right: "8px",
              top: "calc(50% - 8px)",
              transform: "translateY(-50%)",
              zIndex: 2,
            }}
          />
        </div>
      </Button>
    </ShineBorder>
  )
}

export default CloneAgentButton
